import { useState, FormEvent } from 'react';

interface CalculatorProps {
  language: 'pt' | 'en';
}

const motivosPT = [
  'Aquisição imobiliária',
  'Construção / Reforma',
  'Planejamento patrimonial',
  'Estruturação empresarial',
  'Veículo / Frota',
  'Carta contemplada',
  'Outro',
];

const motivosEN = [
  'Real estate acquisition',
  'Construction / Renovation',
  'Wealth planning',
  'Corporate structuring',
  'Vehicle / Fleet',
  'Awarded letter',
  'Other',
];

export default function Calculator({ language }: CalculatorProps) {
  const [formData, setFormData] = useState({
    assetValue: '',
    period: '',
    adminFee: '',
    initialPayment: '',
    motive: '',
    name: '',
    whatsapp: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const content = {
    pt: {
      title: 'Simulador',
      subtitle: 'Pressupostos claros. Sem promessas.',
      disclaimer:
        'Simulação ilustrativa. Contemplação por sorteio ou lance (não garantido). Indexação (ex.: INCC) e taxas conforme regras do grupo.',
      assetValueLabel: 'Valor do ativo',
      assetValuePlaceholder: 'Exemplo: 500.000',
      periodLabel: 'Período (meses)',
      periodPlaceholder: 'Ex: 120',
      adminFeeLabel: 'Taxa administrativa (%)',
      adminFeePlaceholder: 'Ex: 18',
      initialPaymentLabel: 'Pagamento inicial (opcional)',
      initialPaymentPlaceholder: 'Exemplo: 50.000',
      motiveLabel: 'Motivo para o consórcio',
      motiveDefault: 'Selecione um motivo',
      motives: motivosPT,
      nameLabel: 'Nome',
      namePlaceholder: 'Seu nome completo',
      whatsappLabel: 'WhatsApp',
      whatsappPlaceholder: '+55 (11) 9 0000-0000',
      emailLabel: 'E-mail',
      emailPlaceholder: 'seu@email.com',
      submitBtn: 'Solicitar diagnóstico',
      successMessage: 'Solicitação enviada! Nossa equipe retornará em breve.',
    },
    en: {
      title: 'Simulator',
      subtitle: 'Clear assumptions. No promises.',
      disclaimer:
        'Illustrative simulation. Credit allocation by raffle or bid (not guaranteed). Indexation (e.g.: INCC) and fees according to group rules.',
      assetValueLabel: 'Asset value',
      assetValuePlaceholder: 'Example: 500,000',
      periodLabel: 'Period (months)',
      periodPlaceholder: 'Ex: 120',
      adminFeeLabel: 'Administrative fee (%)',
      adminFeePlaceholder: 'Ex: 18',
      initialPaymentLabel: 'Initial payment (optional)',
      initialPaymentPlaceholder: 'Example: 50,000',
      motiveLabel: 'Reason for consortium',
      motiveDefault: 'Select a reason',
      motives: motivosEN,
      nameLabel: 'Name',
      namePlaceholder: 'Your full name',
      whatsappLabel: 'WhatsApp',
      whatsappPlaceholder: '+55 (11) 9 0000-0000',
      emailLabel: 'E-mail',
      emailPlaceholder: 'your@email.com',
      submitBtn: 'Request diagnosis',
      successMessage: 'Request sent! Our team will get back to you shortly.',
    },
  };

  const t = content[language];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const body = new URLSearchParams();
      body.append('assetValue', formData.assetValue);
      body.append('period', formData.period);
      body.append('adminFee', formData.adminFee);
      body.append('initialPayment', formData.initialPayment);
      body.append('motive', formData.motive);
      body.append('name', formData.name);
      body.append('whatsapp', formData.whatsapp);
      body.append('email', formData.email);

      const response = await fetch(
        'https://readdy.ai/api/form/d6tj9ma4of3kuoicn6s0',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        }
      );

      if (response.ok) {
        setSubmitMessage(t.successMessage);
        setFormData({
          assetValue: '',
          period: '',
          adminFee: '',
          initialPayment: '',
          motive: '',
          name: '',
          whatsapp: '',
          email: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] text-sm rounded-md focus:outline-none focus:border-[#F97700] transition-colors placeholder:text-[#B0A89C]';

  return (
    <section className="relative bg-[#F6F2EC] border-b border-[#E7E1D9] overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[780px]">

        {/* ── LEFT: Form column ── */}
        <div className="relative z-10 flex-1 lg:max-w-[58%] flex flex-col justify-center px-8 lg:px-16 py-20 bg-[#F6F2EC]">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="w-10 h-0.5 bg-[#F97700] mx-auto mb-6" />
            <h2 className="text-4xl font-light text-[#0B0B0B] mb-3">{t.title}</h2>
            <p className="text-sm text-[#2E2E2E]">{t.subtitle}</p>
          </div>

          {/* Top disclaimer */}
          <p className="text-xs text-center text-[#6B6057] mb-8 leading-relaxed">
            {t.disclaimer}
          </p>

          {/* Card */}
          <div className="bg-white border border-[#E7E1D9] rounded-lg p-8 shadow-sm">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              {/* Row 1: Valor do ativo + Período */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.assetValueLabel}
                  </label>
                  <input
                    type="text"
                    name="assetValue"
                    value={formData.assetValue}
                    onChange={handleChange}
                    placeholder={t.assetValuePlaceholder}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.periodLabel}
                  </label>
                  <input
                    type="text"
                    name="period"
                    value={formData.period}
                    onChange={handleChange}
                    placeholder={t.periodPlaceholder}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 2: Taxa administrativa + Pagamento inicial */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.adminFeeLabel}
                  </label>
                  <input
                    type="text"
                    name="adminFee"
                    value={formData.adminFee}
                    onChange={handleChange}
                    placeholder={t.adminFeePlaceholder}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.initialPaymentLabel}
                  </label>
                  <input
                    type="text"
                    name="initialPayment"
                    value={formData.initialPayment}
                    onChange={handleChange}
                    placeholder={t.initialPaymentPlaceholder}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 3: Motivo (full width) */}
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                  {t.motiveLabel}
                </label>
                <select
                  name="motive"
                  value={formData.motive}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-[#E7E1D9] text-[#0B0B0B] text-sm rounded-md focus:outline-none focus:border-[#F97700] transition-colors cursor-pointer appearance-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\'%3E%3Cpath fill=\'%232E2E2E\' d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                >
                  <option value="">{t.motiveDefault}</option>
                  {t.motives.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Row 4: Nome (full width) */}
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                  {t.nameLabel}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.namePlaceholder}
                  required
                  className={inputClass}
                />
              </div>

              {/* Row 5: WhatsApp + E-mail */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.whatsappLabel}
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder={t.whatsappPlaceholder}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2E2E2E] mb-1">
                    {t.emailLabel}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Submit button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#F97700] text-white text-base font-semibold rounded-md hover:bg-[#E56E00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '...' : t.submitBtn}
                </button>

                {submitMessage && (
                  <p className="text-center text-sm text-[#F97700] font-medium mt-4">
                    {submitMessage}
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Bottom disclaimer */}
          <p className="text-xs text-center mt-6 leading-relaxed">
            <span className="text-[#F97700]">
              {language === 'pt' ? 'Simulação ilustrativa.' : 'Illustrative simulation.'}
            </span>{' '}
            <span className="text-[#6B6057]">
              {language === 'pt' ? 'Contemplação por sorteio ou' : 'Credit allocation by raffle or'}
            </span>{' '}
            <span className="text-[#F97700]">
              {language === 'pt' ? 'lance' : 'bid'}
            </span>{' '}
            <span className="text-[#6B6057]">
              {language === 'pt' ? '(não garantido).' : '(not guaranteed).'}
            </span>{' '}
            <span className="text-[#F97700]">
              {language === 'pt' ? 'Indexação' : 'Indexation'}
            </span>{' '}
            <span className="text-[#6B6057]">
              {language === 'pt'
                ? '(ex.: INCC) e taxas conforme regras do grupo.'
                : '(e.g.: INCC) and fees according to group rules.'}
            </span>
          </p>
        </div>

        {/* ── RIGHT: Decorative image column ── */}
        <div className="hidden lg:block lg:flex-1 lg:max-w-[42%] relative overflow-hidden">
          {/* Subtle left-fade overlay so the image blends into the form area */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F6F2EC] via-[#F6F2EC]/20 to-transparent z-10" />
          <img
            src="https://public.readdy.ai/ai/img_res/65a70913c11132931cb45ac17acf4fe4.jpg"
            alt=""
            className="w-full h-full object-cover object-left-top"
            style={{ filter: 'grayscale(100%) saturate(0%) brightness(1.08) contrast(0.85)', opacity: 0.55 }}
          />
        </div>

      </div>
    </section>
  );
}
